// C.1 — Нейро-хирург Зейн (livlife_zayn).
//
// xlsx v6 dsl_active (Ф2, 1× за матч):
//   target: ally_aoe_all
//   effect: swap_atk_hp_zero_armor
//   duration: until_end_of_turn
//   → Все союзные ЮНИТЫ (не Герой) получают на 1 ход:
//       atk_temp = base_hp, hp_temp = base_atk, max_armor=0, current_armor=0.
//     В начале следующего хода всё откатывается обратно к base.
//
// Реализация через дельту (вариант A, согласовано с балансером):
//   - Для юнита 3/2/5: новый atk=5, hp=3, armor=0. Дельта = +2/-2/-2.
//   - applyTemporaryBuff(state, unit, {+2,-2,-2}, expiresAtTurn, ...)
//   - Откатывается стандартно в начале следующего хода через expireTemporaryBuffs.
//   - БУГ.21: дельта считается от current.atk/current.hp — фактических текущих
//     статов (включая permanent-баффы, дебаффы и полученный урон). Раньше
//     ошибочно брали base.atk/base.hp, где base.hp = MAX HP (а не остаток).
//     Пример: юнит max 4/2/7, с уроном 4/0/6 → активка даёт 6/0/4 (а не 7/0/3).
//
// xlsx v6 dsl_trigger1:
//   event: turn_end, target: enemy_random, effect: damage_split_per_healed_count
//   value: 1_per_healed_unit_this_turn
//   → В конце своего хода: N разделённых ударов по 1 урона случайным
//     вражеским ЮНИТАМ, где N = сколько разных союзных юнитов получили хил
//     в этом ходу. Если N=0 — триггер не срабатывает.

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { applyTemporaryBuff } from '../../../buffs.js';
import { applyDamage } from '../../../combat.js';
import { registerOnAllyHealedObserver } from '../heal.js';

export const HERO_ID = 'livlife_zayn';

// === Trigger в конце хода: damage_split_per_healed_count ===
// Счётчик «сколько РАЗНЫХ союзных юнитов получили хил в этом ходу» хранится
// в state.players[idx].zaynHealedUnitsThisTurn (Set<instanceId>).

function ensureZaynCounter(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p) return null;
  if (!(p.zaynHealedUnitsThisTurn instanceof Set)) {
    p.zaynHealedUnitsThisTurn = new Set();
  }
  return p.zaynHealedUnitsThisTurn;
}

// Сбрасывается в начале хода Зейна.
export function zaynOnTurnStart(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return;
  if (p.zaynHealedUnitsThisTurn instanceof Set) {
    p.zaynHealedUnitsThisTurn.clear();
  }
}

// Регистрация observer'а хила. При каждом успешном хиле любого союзника
// Зейна — пополняем Set его юнитом.
registerOnAllyHealedObserver(HERO_ID, (state, observer, healEvent) => {
  if (!observer || observer.id !== HERO_ID) return;
  if (observer.current?.silenced) return;
  if (!healEvent?.target) return;
  if (healEvent.target.type !== 'unit') return;
  if ((healEvent.amount || 0) <= 0) return;
  const ownerIdx = observer.ownerId;
  if (ownerIdx == null) return;
  // Союзник? target.ownerId должен быть равен observer.ownerId.
  if (healEvent.target.ownerId !== ownerIdx) return;
  const counter = ensureZaynCounter(state, ownerIdx);
  if (counter) counter.add(healEvent.target.instanceId);
});

// Trigger в конце хода — N разделённых ударов по 1 урона.
export function zaynOnEndOfTurn(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return;
  if (p.hero.current?.silenced) return;
  const counter = ensureZaynCounter(state, playerIdx);
  const N = counter ? counter.size : 0;
  if (N <= 0) return;

  // Цель: N случайных вражеских ЮНИТОВ (не Герой). С повторами или без?
  // По диздоку — «N разделённых ударов случайным вражеским ЮНИТАМ».
  // Берём random with replacement (один и тот же юнит может схлопотать N раз).
  const enemyIdx = 1 - playerIdx;
  const enemy = state.players?.[enemyIdx];
  if (!enemy?.field) return;
  const enemyUnits = [];
  for (const row of enemy.field) {
    if (!row) continue;
    for (const u of row) {
      if (u && (!u.isAlive || u.isAlive())) enemyUnits.push(u);
    }
  }
  if (enemyUnits.length === 0) return;

  let hits = 0;
  for (let i = 0; i < N; i++) {
    const idx = state?.rng?.int ? state.rng.int(enemyUnits.length) : Math.floor(Math.random() * enemyUnits.length);
    const target = enemyUnits[idx];
    if (!target || (target.isAlive && !target.isAlive())) continue;
    applyDamage(target, 1, {
      state,
      source: { id: HERO_ID, name_ru: 'Нейро-хирург Зейн' }
    });
    hits++;
  }
  if (state.log && hits > 0) {
    state.log.add(`💢 Зейн [${p.name}]: ${N} ударов по 1 урона (хил-счётчик)`);
  }
  // Сброс счётчика после применения.
  if (counter) counter.clear();
}

// === Активка Ф2: swap_atk_hp_zero_armor ===
export function zaynActive(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return { ok: false, reason: 'wrong_hero' };
  if (p.heroActiveUsedThisMatch) return { ok: false, reason: 'already_used' };

  const allies = [];
  if (p.field) {
    for (const row of p.field) {
      if (!row) continue;
      for (const u of row) {
        if (u && u.type === 'unit' && (!u.isAlive || u.isAlive())) allies.push(u);
      }
    }
  }

  // Дельта на 1 ход: applyTemporaryBuff истекает в начале хода (currentTurn+1).
  // Семантика: at start of next turn — откатывается.
  const expiresAtTurn = (state.currentTurn ?? 1) + 1;

  let applied = 0;
  for (const u of allies) {
    // Берём ТЕКУЩИЕ статы (с уроном/баффами/дебаффами), а не base.
    const curAtk = u.current?.atk ?? u.base?.atk ?? 0;
    const curHp  = u.current?.hp  ?? u.base?.hp  ?? 0;
    const curArmor = u.persistentMaxArmor ?? 0; // суммарный max armor от шаблона+permanent
    // base.atk/hp в buffs.js включают permanent уже (applyPermanentBuff добавляет к base).
    // Но шаблонный max armor хранится отдельно. Для зануления armor:
    //   - убираем persistentMaxArmor → -curArmor
    //   - current.armor станет 0 (cap по новому max)
    const dAtk = curHp - curAtk;
    const dHp = curAtk - curHp;
    // Armor: чтобы max_armor стал 0, нужно вычесть всё persistentMaxArmor.
    // applyTemporaryBuff с armor=-N снимает с persistentArmor / persistentMaxArmor.
    // Шаблонная броня (template.armor) хранится в template, но в State Resolver
    // current.armor включает persistentMaxArmor. Для занулирования max_armor
    // достаточно вычесть текущее значение max armor.
    const dArmor = -(u.maxArmorWithMods ?? curArmor);

    applyTemporaryBuff(state, u,
      { atk: dAtk, armor: dArmor, hp: dHp },
      expiresAtTurn,
      `${HERO_ID}_swap`,
      'Зейн: подмена atk↔hp'
    );
    applied++;
  }

  p.heroActiveUsedThisMatch = true;
  if (state.log) {
    state.log.add(`🔄 Зейн [${p.name}]: активка — подмена atk↔hp на ${applied} юнитах (1 ход)`);
  }
  return { ok: true, applied };
}

export function zaynActiveAvailable(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return false;
  if (p.heroActiveUsedThisMatch) return false;
  if (p.hero.current?.silenced) return false;
  // Хотя бы один союзный юнит.
  if (!p.field) return false;
  for (const row of p.field) {
    if (!row) continue;
    for (const u of row) if (u && u.type === 'unit' && (!u.isAlive || u.isAlive())) return true;
  }
  return false;
}

registerImplementedAbility(HERO_ID, 'trigger', 'active');
